import React, { useState, useEffect, useCallback } from "react";
import { CloseButtonIcon, Header, ActionLink, usePrevious } from "halifax";
import { Box, Typography } from "@material-ui/core";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RouteComponentProps } from "react-router";
import clsx from "clsx";
import {
  ActivitiesEntryTypeEnum,
  RecentExperienceSearch,
  IResult,
} from "redmond";
import { getSelectionName } from "../../../../utils/selectionHelpers";
import H from "history";
import {
  getExperiencesAvailabilityQuery,
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
  useIsTravelApp,
} from "@capone/common";

import { useAutocompleteAnalytics } from "../../../../hooks/useAutocompleteAnalytics";
import { MobileExperiencesSearchConnectorProps } from "./container";
import "./styles.scss";
import { PATH_AVAILABILITY, PATH_HOME } from "../../../../utils/paths";
import * as textConstants from "../../../common/textConstants";
import { LocationSearch, MobileCalendarPicker } from "../../../common";
import { TripDestination } from "../../types";
import dayjs from "dayjs";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  EXPERIENCES_SEARCH_NEARBY_EXPERIMENT,
  DATELESS_SEARCH_EXPERIMENT,
} from "../../../../context/experiments";

enum MobileExperiencesSearchStep {
  LocationSearch,
  CalendarPicker,
  Complete,
}

export interface IMobileExperiencesSearchControlProps
  extends MobileExperiencesSearchConnectorProps,
    RouteComponentProps {
  onSelectLocation?: (value: any) => void;
  recentSearches?: RecentExperienceSearch[];
  onRecentSearchClick?: (search: RecentExperienceSearch) => void;
}

export const MobileExperiencesSearchControl = (
  props: IMobileExperiencesSearchControlProps,
) => {
  const {
    history,
    searchLocation,
    locationCategories,
    loading,
    hasLocationAutocompleteError,
    setLocationAutocompleteError,
    onSelectLocation,
    setLocation,
    fetchLocationCategories,
    recentSearches,
    onRecentSearchClick,
    tripDestinations,
    tripDestinationsLoading,
  } = props;

  const expState = useExperiments();

  const isSearchNearbyExperiment =
    getExperimentVariant(
      expState.experiments,
      EXPERIENCES_SEARCH_NEARBY_EXPERIMENT,
    ) === AVAILABLE;

  const isDatelessSearchEnabled =
    getExperimentVariant(expState.experiments, DATELESS_SEARCH_EXPERIMENT) ===
    AVAILABLE;

  const tripSuggestionsLoaded =
    !tripDestinationsLoading &&
    tripDestinations.length > 0;

  const {
    resetAnalyticsRefs,
    trackAutocompleteFocus,
    trackAutocompleteTyped,
    trackTripClick,
    trackRecentSearchClick,
    trackLocationSelected,
    trackSearchNearbyClick,
  } = useAutocompleteAnalytics({
    tripSuggestionsLoaded,
    tripDestinationsCount: tripDestinations.length,
    locationCategories,
    hasNearbyResult: false,
    isSearchNearbyExperiment,
  });

  const [from, setFrom] = useState<Date | null>(null);
  const [until, setUntil] = useState<Date | null>(null);

  const [currentStep, setCurrentStep] = useState<MobileExperiencesSearchStep>(
    MobileExperiencesSearchStep.LocationSearch,
  );

  const handleClose = () => {
    history.push(PATH_HOME);
    setLocation(null);
    setFrom(null);
    setUntil(null);
    if (hasLocationAutocompleteError) {
      setLocationAutocompleteError(false);
    }
  };

  const handleGoBack = () => {
    if (currentStep === MobileExperiencesSearchStep.LocationSearch) {
      handleClose();
    }
    setCurrentStep((step) =>
      step > MobileExperiencesSearchStep.LocationSearch ? step - 1 : step,
    );
  };

  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, [currentStep]);

  const previousFrom = usePrevious(from);
  const previousUntil = usePrevious(until);

  React.useEffect(() => {
    travelAppInterface.sendEvent({
      name: TravelAppInterfaceEventName.CALENDAR_LOADED,
      funnel: TravelAppInterfaceFunnelType.ACTIVITIES,
    });
  }, []);

  React.useEffect(() => {
    if (!(from && until) && previousFrom && previousUntil) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATES_RESET,
        funnel: TravelAppInterfaceFunnelType.ACTIVITIES,
      });
    }
  }, [from, until]);

  React.useEffect(() => {
    if (from) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATE_PICKED,
        funnel: TravelAppInterfaceFunnelType.ACTIVITIES,
        data: {
          startDate: dayjs(from).format("YYYY-MM-DD"),
        },
      });
    }
  }, [from]);

  React.useEffect(() => {
    if (until) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATE_PICKED,
        funnel: TravelAppInterfaceFunnelType.ACTIVITIES,
        data: {
          endDate: dayjs(until).format("YYYY-MM-DD"),
        },
      });
    }
  }, [until]);

  React.useEffect(() => {
    if (from && until) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATES_FINALIZED,
        funnel: TravelAppInterfaceFunnelType.ACTIVITIES,
      });
    }
  }, [from, until]);

  const handleSearch = (history: H.History) => {
    history.push(
      `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
        searchLocation?.id ? getSelectionName(searchLocation.id) : "",
        from,
        until || from,
        undefined,
        isTravelApp,
        ActivitiesEntryTypeEnum.HOMEPAGE,
      )}`,
    );
  };

  const { isTravelApp } = useIsTravelApp();

  React.useEffect(() => {
    if (isTravelApp) {
      setCurrentStep(MobileExperiencesSearchStep.CalendarPicker);
    }
  }, [isTravelApp]);

  // Reset analytics refs when leaving LocationSearch step
  useEffect(() => {
    if (currentStep !== MobileExperiencesSearchStep.LocationSearch) {
      resetAnalyticsRefs();
    }
  }, [currentStep, resetAnalyticsRefs]);

  const handleSearchInput = useCallback(
    (queryString: string) => {
      if (queryString.length > 0) {
        trackAutocompleteTyped();
      }
    },
    [trackAutocompleteTyped],
  );

  const handleTripClick = useCallback(
    (trip: TripDestination, index: number) => {
      trackTripClick(index);
      history.push(
        `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
          trip.location.id ? getSelectionName(trip.location.id) : "",
          dayjs(trip.tripDate).toDate(),
          dayjs(trip.tripEndDate).toDate(),
          undefined,
          isTravelApp,
          ActivitiesEntryTypeEnum.UPCOMING_TRIP,
        )}`,
      );
    },
    [history, trackTripClick],
  );

  const handleRecentSearchClick = useCallback(
    (search: RecentExperienceSearch) => {
      const searchIndex = recentSearches?.indexOf(search) ?? 0;
      trackRecentSearchClick(searchIndex);
      onRecentSearchClick?.(search);
    },
    [onRecentSearchClick, recentSearches, trackRecentSearchClick],
  );

  const handleLocationSelected = useCallback(
    (value: IResult) => {
      trackLocationSelected(value.id);
    },
    [trackLocationSelected],
  );

  return (
    <Box
      className={clsx({
        "date-range-picker":
          currentStep === MobileExperiencesSearchStep.CalendarPicker,
      })}
    >
      {!isTravelApp && (
        <Header
          className="homepage"
          center={<HeaderCenterSection currentStep={currentStep} />}
          left={
            <ActionLink
              className={clsx("mobile-experiences-search-header-go-back", {
                hidden:
                  currentStep === MobileExperiencesSearchStep.LocationSearch,
              })}
              onClick={handleGoBack}
              content={<FontAwesomeIcon icon={faChevronLeft} />}
            />
          }
          right={
            <ActionLink
              className="mobile-experiences-search-header-close"
              onClick={handleClose}
              content={<CloseButtonIcon />}
              label="Close"
            />
          }
          isMobile
          fullWidth
        />
      )}
      {currentStep === MobileExperiencesSearchStep.LocationSearch && (
        <LocationSearch
          onComplete={(value: any) => {
            setCurrentStep(MobileExperiencesSearchStep.CalendarPicker);
            onSelectLocation?.(value);
          }}
          locationSearchPrefixString={textConstants.WHERE_TO}
          onClickAutocompleteError={() => {
            history.push(PATH_HOME);
            if (hasLocationAutocompleteError) {
              setLocationAutocompleteError(false);
            }
          }}
          location={searchLocation}
          locationCategories={locationCategories}
          loading={loading}
          hasLocationAutocompleteError={hasLocationAutocompleteError}
          setLocation={setLocation}
          fetchLocationCategories={fetchLocationCategories}
          setLocationAutocompleteError={setLocationAutocompleteError}
          recentSearches={recentSearches}
          onRecentSearchClick={handleRecentSearchClick}
          tripDestinations={tripDestinations}
          tripDestinationsLoading={tripDestinationsLoading}
          onTripClick={handleTripClick}
          onFocusSearch={trackAutocompleteFocus}
          onBlurSearch={resetAnalyticsRefs}
          onSearchInput={handleSearchInput}
          onLocationSelected={handleLocationSelected}
          onSearchNearbyClicked={trackSearchNearbyClick}
        />
      )}
      {currentStep === MobileExperiencesSearchStep.CalendarPicker && (
        <MobileCalendarPicker
          fromDate={from}
          untilDate={until}
          setFromDate={setFrom}
          setUntilDate={setUntil}
          handleSearch={handleSearch}
          showButton={!isTravelApp}
          isDatelessSearch={isDatelessSearchEnabled}
        />
      )}
    </Box>
  );
};

interface IHeaderCenterSectionProps {
  currentStep: number;
}

const HeaderCenterSection = ({ currentStep }: IHeaderCenterSectionProps) => {
  const headerText =
    currentStep === MobileExperiencesSearchStep.CalendarPicker
      ? textConstants.UPDATE_CALENDAR_TEXT
      : textConstants.EXPERIENCES_HEADER;
  return (
    <Box className="header-center-section">
      {headerText === textConstants.UPDATE_CALENDAR_TEXT ? (
        <Box className="calendar-header">
          <Typography variant="body1">{headerText}</Typography>
        </Box>
      ) : (
        <Box className="mobile-experiences-search-location-label">
          <Typography variant="body1">{headerText}</Typography>
        </Box>
      )}
    </Box>
  );
};
